import type { Image } from "../../../../../aysa_api/models/image.types";
import type { Outfit } from "../../../../../aysa_api/models/outfit.types";
import type { Idol } from "../../../../../aysa_api/models/idol.types";
import type { Story } from "../../../../../aysa_api/models/story.types";
import type { CMSTeaser } from "../../../../../aysa_api/models/cms_teaser.types";
import type { ProductTile } from "../../../../../aysa_api/models/product_tile.types";
import type { Link } from "../../../../../aysa_api/models/link.types";
import type { SectionTracker } from "../../../../../aysa_api/models/tracker.types";
import type { ProductSection } from "../../../../../aysa_api/models/product_section.types";
import type { Advertisement } from "../../../../../aysa_api/models/advertisement.types";
import type { StreamProgress } from "../../../../../aysa_api/models/stream_progress.types";
import type { MissingAttributesHint } from "../../../../../aysa_api/models/size_finder.types";
import type { OutfitSliderSection } from "../../../../../aysa_api/models/sections.types";

export enum TeaserTileCarousel_TextPosition {
  // default text position - TEXT_POSITION_BELOW_IMAGE = 2
  UNSPECIFIED = 0,
  // Referencing other story reco lanes
  OVER_IMAGE = 1,
  // Referencing Guided story recolanes
  BELOW_IMAGE = 2,
}

export interface CategoryStreamItem {
  // The position of the item within the stream
  //
  // This position is 0-based and is independent of any row/column layout
  streamPosition: number;
  type?:
    | {
        $case: "productSection";
        productSection: ProductSection;
      }
    | {
        $case: "outfitRecommendation";
        // For web desktop
        outfitRecommendation: OutfitRecommendation;
      }
    | {
        $case: "deprecatedStoryRecommendation";
        deprecatedStoryRecommendation: StoriesRecommendation;
      }
    | {
        $case: "streamAdvertisement";
        streamAdvertisement: Advertisement;
      }
    | {
        $case: "deprecatedGuidesStoriesRecommendation";
        deprecatedGuidesStoriesRecommendation: GuidesStoriesRecommendation;
      }
    | {
        $case: "streamDescription";
        streamDescription: StreamDescription;
      }
    | {
        $case: "streamProgress";
        streamProgress: StreamProgress;
      }
    | {
        $case: "uspBanner";
        uspBanner: USPBanner;
      }
    | {
        $case: "sizeFinderHint";
        // Returned in case the client has Size Finder enabled
        // but is missing some required attributes for suggestions.
        sizeFinderHint: MissingAttributesHint;
      }
    | {
        $case: "automaticUserSizesBanner";
        automaticUserSizesBanner: AutomaticUserSizesBanner;
      }
    | {
        $case: "filterQuestionnaire";
        filterQuestionnaire: FilterQuestionnaireBanner;
      }
    | {
        $case: "teaserTileCarousel";
        // Slider / Carousel UI of the teaser
        teaserTileCarousel: TeaserTileCarousel;
      }
    | {
        $case: "teaserTileGrid";
        teaserTileGrid: TeaserTileGrid;
      }
    | {
        $case: "pushNotificationTrigger";
        // Once visible in the stream it should trigger the
        // push notification soft ask flyout, only for mWeb
        pushNotificationTrigger: PushNotificationTrigger;
      }
    | {
        $case: "inStreamFilter";
        // Both following fields deprecated
        // https://aboutyou.atlassian.net/browse/ASBE-2156
        // https://aboutyou.atlassian.net/browse/ASBE-2232
        inStreamFilter: InStreamFilter;
      }
    | {
        $case: "fashionUpdate";
        fashionUpdate: FashionUpdate;
      }
    | {
        $case: "inStreamPushNotificationTrigger";
        // Once visible in the stream it should trigger the
        // push notification soft ask, only for mWeb
        inStreamPushNotificationTrigger: InStreamPushNotificationTrigger;
      }
    | {
        $case: "outfitSlider";
        // Outfit recommendation
        outfitSlider: OutfitSliderSection;
      };
}

// FilterQuestionnaireBanner is a banner used to advertise a filter questionnaire on a category page.
//
// For flexibility it is provided as an image.
// When the user clicks on it, the `aysa_api.models.Questionnaire` should be opened
// with the current category identifier.
export interface FilterQuestionnaireBanner {
  banner: Image;
  logo?: Image;
  headline?: string;
  subline?: string;
}

// For web desktop
export interface OutfitRecommendation {
  outfit: Outfit;
  idol: Idol;
}

// A special recommendation for stories which are of the type guides
export interface GuidesStoriesRecommendation {
  stories: Story[];
}

export interface StoriesRecommendation {
  stories: Story[];
}

export interface TeaserTileCarousel {
  headline?: string;
  subline?: string;
  textPosition: TeaserTileCarousel_TextPosition;
  teaser: CMSTeaser[];
}

export interface TeaserTileGrid {
  headline: string;
  subline: string;
  teaser: CMSTeaser[];
}

export interface StreamDescription {
  type?:
    | {
        $case: "endOfStreamDescription";
        // This is end of stream text that may include selected category or selected filter labels
        endOfStreamDescription: string;
      }
    | {
        $case: "additionalStreamTitle";
        // Stream title that may include main and additional category titles as well as selected filters titles
        additionalStreamTitle: string;
      };
}

export interface USPBanner {
  usps: USPBanner_USP[];
}

export interface USPBanner_USP {
  label: string;
  image: Image;
}

export interface AutomaticUserSizesBanner {
  type?:
    | {
        $case: "suggestSizes";
        suggestSizes: AutomaticUserSizesBanner_SuggestSizes;
      }
    | {
        $case: "appliedSizes";
        appliedSizes: AutomaticUserSizesBanner_AppliedSizes;
      };
}

export interface AutomaticUserSizesBanner_Size {
  // The ID which is being used as a filter and saved on BI
  id: number;
  // The label for the Size
  label: string;
}

// Suggest the user to save the sizes to his account
// as they are different to the ones currently saved.
//
// https://aboutyou.atlassian.net/wiki/spaces/AYSA/pages/861274510/User+Sizes#1.-Save-size-suggestion
export interface AutomaticUserSizesBanner_SuggestSizes {
  sizes: AutomaticUserSizesBanner_Size[];
}

// Lets the user know in case we applied sizes automatically for him based on his User Sizes from BI.
//
// https://aboutyou.atlassian.net/wiki/spaces/AYSA/pages/861274510/User+Sizes#2.-Size-Auto-Filter
export interface AutomaticUserSizesBanner_AppliedSizes {
  sizes: AutomaticUserSizesBanner_Size[];
}

export interface PushNotificationTrigger {}

export interface InStreamPushNotificationTrigger {}

// https://aboutyou.atlassian.net/browse/DT-4587
export interface InStreamFilter {
  type?:
    | {
        $case: "v1";
        v1: InStreamFilter_Variant1;
      }
    | {
        $case: "v2";
        v2: InStreamFilter_Variant2;
      };
}

export interface InStreamFilter_Variant1 {
  // eg: Are you interested in?
  headline: string;
  // eg: High waisted pants?
  subline: string;
  products: ProductTile[];
  // Eg: Apply filter (link_target includes the filter)
  cta: Link;
  // eg: section { name: 'instream_filters'}
  tracker: SectionTracker;
}

export interface InStreamFilter_Variant2 {
  // eg: Raises to discover
  headline: string;
  tabs: InStreamFilter_Variant2_Tab[];
  // eg: section { name: 'instream_filters'}
  tracker: SectionTracker;
}

export interface InStreamFilter_Variant2_Tab {
  // eg: hight waist | mid waist | low waist | super low waist
  headline: string;
  products: ProductTile[];
  // Eg: Discover all (link_target includes the filter)
  cta: Link;
}

export interface FashionUpdate {
  // eg: Picked for you
  headline: string;
  productTiles: ProductTile[];
  tracker: SectionTracker;
}
